import React, { memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Checkbox } from '@tcc/crate';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getMessage } from 'util/locale';
import { setAllSelectedLayers, setSelectedLayerGroup } from 'manual';
import {
  getImageDataLayers,
  getSelectedLayerGroup,
  getSelectedLayers,
  getSelectedMapLayer,
  getYearsFromCciLayers
} from 'manual/selectors';
import { CCILayer } from 'store/types/types';
import { getLayersGroupCheckBoxStates } from 'manual/util/zone-generation';
import ZoneGenerationNoData from './ZoneGenerationNoData';

interface YearItemProps {
  year: string;
  isManualFlowLowQualityLayers: boolean;
}

const YearItem = ({ year, isManualFlowLowQualityLayers }: YearItemProps) => {
  const dispatch = useAppDispatch();
  const selectedLayerGroup = useAppSelector(getSelectedLayerGroup);
  const imageDataLayers = useAppSelector(getImageDataLayers(year));
  const selectedLayers = useAppSelector(getSelectedLayers);
  const { selectedMapType } = useAppSelector(getSelectedMapLayer);
  const allDataLayersByYear = imageDataLayers?.map(([, items]) => items)?.flat();
  const allUsabledDataLayersByYearCount = useMemo(() => {
    if (isManualFlowLowQualityLayers) {
      return allDataLayersByYear?.length || 0;
    }
    return allDataLayersByYear?.filter(({ isUsable }) => isUsable).length || 0;
  }, [allDataLayersByYear, isManualFlowLowQualityLayers]);
  const selectCciYear = (year: string) => {
    dispatch(setSelectedLayerGroup(year));
  };
  const selectAllLayers = useCallback(
    (isSelected?: boolean) => {
      dispatch(setAllSelectedLayers(isSelected, 'cci', year));
    },
    [dispatch, year]
  );

  const selectedLayersByYearCount = useMemo(
    () => selectedLayers?.filter((selectedLayer: CCILayer) => selectedLayer.year?.toString() === year)?.length,
    [selectedLayers, year]
  );
  const isSelectedYear = selectedLayerGroup === year;
  const {
    isChecked: isSelected,
    isDisabled,
    isIndeterminate
  } = getLayersGroupCheckBoxStates({
    groupUsableLayersCount: allUsabledDataLayersByYearCount,
    selectedGroupLayersCount: selectedLayersByYearCount,
    allSelectedLayersCount: selectedLayers?.length,
    selectedMapType
  });

  return (
    <li
      key={year}
      className={classNames({ isSelected: isSelectedYear })}
      data-test-id={`cci-year-item-${year}`}
      onClick={isSelectedYear ? undefined : () => selectCciYear(year)}
    >
      <Checkbox
        checked={isSelected}
        disabled={isDisabled}
        isIndeterminate={isIndeterminate}
        onChange={() => selectAllLayers(isSelected)}
        testId={`select-all-checkbox-${year}`}
      />
      {year}
    </li>
  );
};

interface ZoneGenerationCciYearSelectionProps {
  isManualFlowLowQualityLayers: boolean;
}

const ZoneGenerationCciYearSelection = ({ isManualFlowLowQualityLayers }: ZoneGenerationCciYearSelectionProps) => {
  const years = useAppSelector(getYearsFromCciLayers);

  if (!years.length) {
    return <ZoneGenerationNoData message={getMessage('text__generate-zones-layers-no-fhi-data')} />;
  }

  return (
    <>
      <section data-test-id='layers-groups-header'>{getMessage('label-text__generate-zones-layers-cci-year')}</section>
      <ul data-test-id='cci-year-group-list'>
        {years.map((year) => (
          <YearItem key={year} year={year} isManualFlowLowQualityLayers={isManualFlowLowQualityLayers} />
        ))}
      </ul>
    </>
  );
};

export default memo(ZoneGenerationCciYearSelection);
